import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Trash2, Plus, AlertTriangle, Wind } from "lucide-react";
import { toast } from "sonner";

/**
 * ScopeBuilder — constructor structurat „Lucrări de executat" pentru un proiect.
 *
 * Înlocuiește textarea „Ce este de făcut" cu o listă de zone tipizate
 * (cameră + material de scos + cantitate) cu auto-clasificare în EKAS/Factsheet/Merkblatt.
 *
 * Props:
 *  - value: array de scope_items (controlled)
 *  - onChange: (newScopeItems) => void
 *  - isRenovation: bool — controlat extern; influențează regulile Luftmessung
 *  - onIsRenovationChange: (bool) => void
 */

// Mirror al constantelor din backend/scope_classifier.py — păstrate sincronizate manual.
// Lista finală provine din /api/scope-catalog (include + custom adăugate de user).

function classifyScopeItem(item, isRenovation) {
  const material = (item.material || "").trim();
  const qty = Number(item.qty || 0);
  const opts = item.options || {};
  let cls = null;

  switch (material) {
    case "fliessenkleber":
      cls = qty > 5
        ? { type: "EKAS", doc_number: null, label: "EKAS" }
        : { type: "Factsheet", doc_number: "33077", label: "Factsheet 33077" };
      break;
    case "cushion_vinyl":
      cls = { type: "EKAS", doc_number: null, label: "EKAS" }; break;
    case "floor_flex": {
      const k = (opts.floor_flex_kleber || "").toLowerCase();
      if (k === "bituminos") cls = { type: "Factsheet", doc_number: "33049", label: "Factsheet 33049" };
      else if (k === "mortelartig") cls = { type: "EKAS", doc_number: null, label: "EKAS" };
      else cls = { type: "Altele", doc_number: null, label: "?" };
      break;
    }
    case "lap_platte": {
      const zfrei = !!opts.lap_zerstorungsfrei;
      const lipita = !!opts.lap_lipita;
      if (zfrei) cls = { type: "Merkblatt", doc_number: "84043", label: "Merkblatt 84043" };
      else if (lipita || qty > 0.5) cls = { type: "EKAS", doc_number: null, label: "EKAS" };
      else cls = { type: "Factsheet", doc_number: "33036", label: "Factsheet 33036" };
      break;
    }
    case "afz_platte":     cls = { type: "Factsheet", doc_number: "33031", label: "Factsheet 33031" }; break;
    case "afz_rohre":      cls = { type: "Merkblatt", doc_number: "84053", label: "Merkblatt 84053" }; break;
    case "deckputz":       cls = { type: "EKAS", doc_number: null, label: "EKAS" }; break;
    case "grundputz":      cls = { type: "EKAS", doc_number: null, label: "EKAS" }; break;
    case "rohrmortel":     cls = { type: "EKAS", doc_number: null, label: "EKAS" }; break;
    case "dichtungen":     cls = { type: "EKAS", doc_number: null, label: "EKAS" }; break;
    case "schnurre":       cls = { type: "Merkblatt", doc_number: "84053", label: "Merkblatt 84053" }; break;
    case "holzzementboden":cls = { type: "EKAS", doc_number: null, label: "EKAS" }; break;
    case "pcb":            cls = { type: "Factsheet", doc_number: "33111+33112", label: "Factsheet 33111 + 33112" }; break;
    case "deckenplatte":   cls = { type: "EKAS", doc_number: null, label: "EKAS" }; break;
    case "fensterkitt":    cls = { type: "Factsheet", doc_number: "33040", label: "Factsheet 33040" }; break;
    case "anschlagkitt":   cls = { type: "Factsheet", doc_number: "33044", label: "Factsheet 33044" }; break;
    case "kork": {
      const pak = !!opts.kork_pak_haltig;
      if (pak && qty > 5) cls = { type: "Factsheet", doc_number: "33106", label: "Factsheet 33106" };
      else if (pak) cls = { type: "EKAS", doc_number: null, label: "EKAS" };
      else cls = { type: "Altele", doc_number: null, label: "Altele" };
      break;
    }
    default:
      // Material custom — folosește clasificarea manuală sau default
      if (item.manual_classification?.type) {
        cls = {
          type: item.manual_classification.type,
          doc_number: item.manual_classification.doc_number || null,
          label: item.manual_classification.label || item.manual_classification.type,
        };
      } else {
        cls = { type: "Altele", doc_number: null, label: "Altele" };
      }
  }

  // Color map
  const colorMap = { EKAS: "#DC2626", Factsheet: "#EA580C", Merkblatt: "#71717A", Altele: "#3B82F6" };
  cls.color = colorMap[cls.type] || "#71717A";

  // Luftmessung
  let luft = false;
  if (isRenovation) {
    if (cls.label === "EKAS") luft = true;
    else if (cls.type === "Factsheet" && !(cls.doc_number === "33077")) luft = true;
  }
  cls.requires_luftmessung = luft;
  return cls;
}

function ScopeRowBadge({ classification }) {
  const c = classification;
  if (!c) return null;
  return (
    <span
      className="text-[10px] px-2 py-1 rounded font-extrabold uppercase tracking-wider whitespace-nowrap"
      style={{ background: c.color, color: "white" }}
    >
      {c.label}
    </span>
  );
}

export default function ScopeBuilder({ value, onChange, isRenovation, onIsRenovationChange }) {
  const [catalog, setCatalog] = useState({ standard_rooms: [], custom_rooms: [], standard_materials: [], custom_materials: [] });

  // Migrare automată: items fără zone_index → zone_index=1
  const items = useMemo(() => {
    const arr = Array.isArray(value) ? value : [];
    return arr.map((it) => ({ ...it, zone_index: Number(it.zone_index) > 0 ? Number(it.zone_index) : 1 }));
  }, [value]);

  // Numărul de zone existente (max zone_index + zone goale tracked separat în state)
  const [explicitZones, setExplicitZones] = useState(1); // Câte zone vrea utilizatorul (chiar dacă goale)
  useEffect(() => {
    const maxFromItems = items.reduce((acc, it) => Math.max(acc, Number(it.zone_index) || 1), 1);
    setExplicitZones((prev) => Math.max(prev, maxFromItems));
  }, [items]);

  useEffect(() => {
    api.get("/scope-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const allRooms = useMemo(() => [...(catalog.standard_rooms || []), ...(catalog.custom_rooms || [])], [catalog]);
  const allMaterials = useMemo(() => [...(catalog.standard_materials || []), ...(catalog.custom_materials || [])], [catalog]);
  const matByKey = useMemo(() => Object.fromEntries(allMaterials.map((m) => [m.key, m])), [allMaterials]);

  const numZones = Math.max(explicitZones, items.reduce((acc, it) => Math.max(acc, Number(it.zone_index) || 1), 1));

  const addRow = (zoneIndex = 1) => {
    onChange([...items, { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, zone_index: zoneIndex, room: "", location_type: "", material: "", qty: "", unit: "m²", options: {} }]);
  };
  const updateRow = (globalIdx, patch) => {
    const next = [...items];
    next[globalIdx] = { ...next[globalIdx], ...patch };
    // Re-clasifică imediat
    next[globalIdx].classification = classifyScopeItem(next[globalIdx], isRenovation);
    onChange(next);
  };
  const removeRow = (globalIdx) => onChange(items.filter((_, i) => i !== globalIdx));

  const addZone = () => {
    setExplicitZones((n) => n + 1);
  };
  const removeZone = (zoneIdx) => {
    // Șterge toate item-urile din zona zoneIdx + renumerotează zonele de deasupra
    const next = items
      .filter((it) => Number(it.zone_index) !== zoneIdx)
      .map((it) => ({ ...it, zone_index: Number(it.zone_index) > zoneIdx ? Number(it.zone_index) - 1 : Number(it.zone_index) }));
    onChange(next);
    setExplicitZones((n) => Math.max(1, n - 1));
  };

  const addCustomRoom = async () => {
    const name = window.prompt("Nume cameră nouă:");
    if (!name) return;
    try {
      await api.post("/scope-catalog/rooms", { name: name.trim() });
      const r = await api.get("/scope-catalog"); setCatalog(r.data);
      toast.success(`Cameră „${name}" adăugată în catalog`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  const addCustomMaterial = async () => {
    const label = window.prompt("Nume material nou:");
    if (!label) return;
    const key = label.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const unit = window.prompt("Unitate (m² / m / buc)?", "buc") || "buc";
    try {
      await api.post("/scope-catalog/materials", { key, label: label.trim(), unit });
      const r = await api.get("/scope-catalog"); setCatalog(r.data);
      toast.success(`Material „${label}" adăugat în catalog`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  // Aggregation pentru sumar (la nivel de proiect)
  const aggregation = useMemo(() => {
    const groups = {};
    let anyLuft = false;
    let totalQty = 0;
    items.forEach((it) => {
      const cls = it.classification || classifyScopeItem(it, isRenovation);
      if (cls?.requires_luftmessung) anyLuft = true;
      const label = cls?.label || "Altele";
      if (!groups[label]) groups[label] = { ...cls, count: 0 };
      groups[label].count += 1;
      const q = Number(it.qty || 0);
      if (!Number.isNaN(q)) totalQty += q;
    });
    const arr = Object.values(groups);
    const typePri = { EKAS: 0, Factsheet: 1, Merkblatt: 2, Altele: 3 };
    arr.sort((a, b) => (typePri[a.type] || 9) - (typePri[b.type] || 9) || (a.label || "").localeCompare(b.label || ""));
    return { groups: arr, anyLuft, totalQty };
  }, [items, isRenovation]);

  // Agregare per zonă — sume cantități + clasificare + Luftmessung pe zonă
  const zoneSummaries = useMemo(() => {
    const summaries = {};
    for (let z = 1; z <= numZones; z++) {
      summaries[z] = { zone_index: z, items_count: 0, total_qty: 0, groups: {}, anyLuft: false };
    }
    items.forEach((it) => {
      const z = Number(it.zone_index) || 1;
      if (!summaries[z]) summaries[z] = { zone_index: z, items_count: 0, total_qty: 0, groups: {}, anyLuft: false };
      summaries[z].items_count += 1;
      const q = Number(it.qty || 0);
      if (!Number.isNaN(q)) summaries[z].total_qty += q;
      const cls = it.classification || classifyScopeItem(it, isRenovation);
      if (cls?.requires_luftmessung) summaries[z].anyLuft = true;
      const label = cls?.label || "Altele";
      if (!summaries[z].groups[label]) summaries[z].groups[label] = { ...cls, count: 0 };
      summaries[z].groups[label].count += 1;
    });
    return summaries;
  }, [items, numZones, isRenovation]);

  return (
    <div className="space-y-3" data-testid="scope-builder">
      {/* Tip lucrare */}
      <div className="flex items-center gap-3 p-3 rounded border border-blue-200 bg-blue-50/50">
        <Switch
          checked={!!isRenovation}
          onCheckedChange={(v) => onIsRenovationChange?.(v)}
          data-testid="is-renovation-switch"
        />
        <div className="flex-1">
          <div className="font-semibold text-sm">{isRenovation ? "🔧 Renovare (existent)" : "🏗 Construcție nouă (Neubau)"}</div>
          <div className="text-[11px] text-zinc-600">
            {isRenovation
              ? "La renovare, EKAS necesită Luftmessung obligatorie după finalizare → demontare 1 zi după end_date."
              : "Construcție nouă — fără Luftmessung obligatorie."}
          </div>
        </div>
      </div>

      {/* Lista lucrări grupată pe zone */}
      <div className="space-y-4">
        {items.length === 0 && numZones === 1 && (
          <div className="text-sm text-zinc-400 italic p-4 border border-dashed rounded text-center">
            Nicio lucrare adăugată. Începe cu „➕ Adaugă lucrare" pentru a specifica camera, materialul și cantitatea.
          </div>
        )}

        {Array.from({ length: numZones }, (_, z) => z + 1).map((zoneIdx) => {
          const zoneItems = items.map((it, gi) => ({ it, gi })).filter(({ it }) => Number(it.zone_index) === zoneIdx);
          const zSum = zoneSummaries[zoneIdx] || { items_count: 0, total_qty: 0, groups: {}, anyLuft: false };
          const zoneGroupsArr = Object.values(zSum.groups || {});
          const typePri = { EKAS: 0, Factsheet: 1, Merkblatt: 2, Altele: 3 };
          zoneGroupsArr.sort((a, b) => (typePri[a.type] || 9) - (typePri[b.type] || 9));
          return (
            <div key={zoneIdx} className="rounded-lg border-2 border-zinc-300 bg-zinc-50/50 overflow-hidden" data-testid={`scope-zone-${zoneIdx}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-blue-100 to-blue-50 border-b-2 border-blue-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-extrabold text-blue-900">📍 Zona {zoneIdx}</span>
                  <span className="text-xs text-zinc-600 font-mono">
                    {zSum.items_count} lucrare{zSum.items_count !== 1 ? "/i" : ""} · {zSum.total_qty.toFixed(2)} u
                  </span>
                  {zoneGroupsArr.map((g) => (
                    <span key={g.label} className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" style={{ background: g.color, color: "white" }} data-testid={`scope-zone-${zoneIdx}-cls-${g.label}`}>
                      {g.label}
                    </span>
                  ))}
                  {zSum.anyLuft && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-200 text-blue-900 font-bold uppercase tracking-wider inline-flex items-center gap-1">
                      <Wind size={9}/> Luftmessung
                    </span>
                  )}
                </div>
                {numZones > 1 && (
                  <button
                    type="button"
                    onClick={() => removeZone(zoneIdx)}
                    className="text-[10px] px-2 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-50 font-semibold"
                    title="Șterge zona și toate lucrările din ea"
                    data-testid={`scope-zone-${zoneIdx}-remove`}
                  >
                    🗑 Șterge zona
                  </button>
                )}
              </div>

              <div className="p-3 space-y-2">
                {zoneItems.length === 0 && (
                  <div className="text-xs text-zinc-400 italic py-2 text-center">
                    Nicio lucrare în această zonă. Apasă „➕ Adaugă lucrare" mai jos.
                  </div>
                )}
                {zoneItems.map(({ it, gi }) => {
                  const i = gi;
                  const mat = matByKey[it.material];
                  const cls = it.classification || classifyScopeItem(it, isRenovation);
                  return (
                    <div key={it.id || i} className="p-3 rounded border border-zinc-200 bg-white space-y-2" data-testid={`scope-row-${i}`}>
                      <div className="grid grid-cols-12 gap-2 items-start">
                        {/* Cameră */}
                        <div className="col-span-12 sm:col-span-3">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Cameră</Label>
                          <Select value={it.room || "__none__"} onValueChange={(v) => updateRow(i, { room: v === "__none__" ? "" : v === "__add__" ? (addCustomRoom(), it.room) : v })}>
                            <SelectTrigger className="mt-1 h-9" data-testid={`scope-room-${i}`}><SelectValue placeholder="—"/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Selectează —</SelectItem>
                              {allRooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                              <SelectItem value="__add__">➕ Adaugă cameră nouă…</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Tip locație opțional */}
                        <div className="col-span-12 sm:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Tip locație</Label>
                          <Input value={it.location_type || ""} onChange={(e) => updateRow(i, { location_type: e.target.value })} className="mt-1 h-9" placeholder="opțional" data-testid={`scope-location-${i}`}/>
                        </div>
                        {/* Material */}
                        <div className="col-span-12 sm:col-span-3">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Material de scos</Label>
                          <Select value={it.material || "__none__"} onValueChange={(v) => {
                            if (v === "__add__") { addCustomMaterial(); return; }
                            if (v === "__none__") { updateRow(i, { material: "", unit: "m²" }); return; }
                            const m = matByKey[v];
                            updateRow(i, { material: v, material_label: m?.label || v, unit: m?.unit || "m²" });
                          }}>
                            <SelectTrigger className="mt-1 h-9" data-testid={`scope-material-${i}`}><SelectValue placeholder="—"/></SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              <SelectItem value="__none__">— Selectează —</SelectItem>
                              {allMaterials.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}{m.is_custom ? " ⭐" : ""}</SelectItem>)}
                              <SelectItem value="__add__">➕ Adaugă material nou…</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Cantitate + unit */}
                        <div className="col-span-7 sm:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Cantitate</Label>
                          <Input type="number" step="0.01" min={0} value={it.qty ?? ""} onChange={(e) => updateRow(i, { qty: e.target.value })} className="mt-1 h-9" placeholder="0" data-testid={`scope-qty-${i}`}/>
                        </div>
                        <div className="col-span-3 sm:col-span-1">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Unit</Label>
                          <div className="mt-1 h-9 flex items-center justify-center text-xs font-bold text-zinc-700 bg-zinc-100 rounded border border-zinc-200">{it.unit || mat?.unit || "m²"}</div>
                        </div>
                        {/* Delete */}
                        <div className="col-span-2 sm:col-span-1 flex items-end justify-end h-full">
                          <button type="button" onClick={() => removeRow(i)} className="text-zinc-400 hover:text-red-600 p-2" title="Șterge" data-testid={`scope-remove-${i}`}>
                            <Trash2 size={16}/>
                          </button>
                        </div>
                      </div>

                      {/* Opțiuni dinamice */}
                      {it.material === "floor_flex" && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-3">
                          <span className="text-xs font-bold text-amber-800">⚠️ Tip kleber:</span>
                          <Select value={it.options?.floor_flex_kleber || "__"} onValueChange={(v) => updateRow(i, { options: { ...(it.options || {}), floor_flex_kleber: v === "__" ? "" : v } })}>
                            <SelectTrigger className="h-8 w-[200px]" data-testid={`scope-floorflex-kleber-${i}`}><SelectValue placeholder="Selectează..."/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__">— Selectează —</SelectItem>
                              <SelectItem value="bituminos">Bituminös</SelectItem>
                              <SelectItem value="mortelartig">Mörtelartig</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {it.material === "lap_platte" && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 flex items-center flex-wrap gap-3">
                          <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={!!it.options?.lap_zerstorungsfrei} onChange={(e) => updateRow(i, { options: { ...(it.options || {}), lap_zerstorungsfrei: e.target.checked } })} data-testid={`scope-lap-zfrei-${i}`}/>Einzelnobjekt zerstörungsfrei</label>
                          <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={!!it.options?.lap_lipita} onChange={(e) => updateRow(i, { options: { ...(it.options || {}), lap_lipita: e.target.checked } })} data-testid={`scope-lap-lipita-${i}`}/>Lipită</label>
                        </div>
                      )}
                      {it.material === "kork" && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-3">
                          <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={!!it.options?.kork_pak_haltig} onChange={(e) => updateRow(i, { options: { ...(it.options || {}), kork_pak_haltig: e.target.checked } })} data-testid={`scope-kork-pak-${i}`}/>PAK-haltig</label>
                        </div>
                      )}

                      {/* Clasificare */}
                      {it.material && (
                        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-zinc-100">
                          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">→ Clasificare:</span>
                          <ScopeRowBadge classification={cls}/>
                          {cls?.requires_luftmessung && (
                            <span className="text-[10px] px-2 py-1 rounded bg-blue-100 text-blue-800 font-bold uppercase tracking-wider flex items-center gap-1">
                              <Wind size={10}/> Luftmessung obligatorie
                            </span>
                          )}
                          {cls?.label === "?" && (
                            <span className="text-[10px] text-amber-700 italic">Selectează tipul de kleber pentru clasificare</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <Button type="button" variant="outline" size="sm" onClick={() => addRow(zoneIdx)} className="w-full border-dashed text-xs" data-testid={`scope-add-row-zone-${zoneIdx}`}>
                  <Plus size={14} className="mr-1"/> Adaugă lucrare în Zona {zoneIdx}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" onClick={addZone} className="w-full border-dashed border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold" data-testid="scope-add-zone">
        <Plus size={16} className="mr-1"/> Adaugă o zonă nouă
      </Button>

      {/* Sumar */}
      {items.length > 0 && (
        <div className="card-flat p-3 bg-zinc-50" data-testid="scope-global-summary">
          <div className="text-xs font-bold text-zinc-700 mb-2 uppercase tracking-wider flex items-center gap-2">
            <span>Rezumat proiect (auto-calculat):</span>
            <span className="text-[11px] px-2 py-0.5 rounded bg-blue-600 text-white font-extrabold" data-testid="scope-total-zones">
              🟦 {numZones} zon{numZones === 1 ? "ă" : "e"}
            </span>
            <span className="text-[11px] px-2 py-0.5 rounded bg-zinc-700 text-white font-extrabold" data-testid="scope-total-qty">
              ∑ {aggregation.totalQty.toFixed(2)} u
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {aggregation.groups.map((g) => (
              <span key={g.label} className="text-[11px] px-2.5 py-1 rounded font-bold uppercase tracking-wider" style={{ background: g.color, color: "white" }} data-testid={`scope-zone-summary-${g.label}`}>
                {g.label}
              </span>
            ))}
          </div>
          {aggregation.anyLuft && (
            <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 mt-2">
              <AlertTriangle size={14} className="flex-shrink-0"/>
              <span><strong>Luftmessung obligatorie</strong> după finalizare → demontare 1 zi după end_date va fi forțată.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
